import React, { useEffect, useRef, useState } from "react";
import { toast } from "react-hot-toast";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
  CircularProgress,
} from "@mui/material";
import axios from "axios";
import { getAuthToken } from "../../utils/getAuthToken";

// Calling has its own dedicated single-instance service — falls back to the
// main API server only if REACT_APP_CALLING_URL is unset (e.g. not yet configured).
const SERVER_DOMAIN = process.env.REACT_APP_CALLING_URL || process.env.REACT_APP_SERVER_DOMAIN || "http://localhost:8000";

const BRIDGE_MODES = [
  { value: "direct", label: "Standard", description: "Agent's browser connects directly to the caller. No greeting possible." },
  { value: "sfu-ring", label: "Ring with bridge", description: "Same ring-and-accept flow, audio passes through our server. No greeting — the call isn't answered yet." },
  { value: "sfu-auto", label: "Auto-answer with greeting", description: "Call connects instantly; the caller hears your greeting/hold audio while agents are rung." },
];

async function authGet(path) {
  const token = await getAuthToken();
  const res = await axios.get(`${SERVER_DOMAIN}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
}
async function authPost(path, body) {
  const token = await getAuthToken();
  const res = await axios.post(`${SERVER_DOMAIN}${path}`, body, {
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
  });
  return res.data;
}
async function authUpload(path, file) {
  const token = await getAuthToken();
  const formData = new FormData();
  formData.append("file", file);
  const res = await axios.post(`${SERVER_DOMAIN}${path}`, formData, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return res.data;
}
async function authDelete(path) {
  const token = await getAuthToken();
  const res = await axios.delete(`${SERVER_DOMAIN}${path}`, { headers: { Authorization: `Bearer ${token}` } });
  return res.data;
}

export default function CallsSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [callingEnabled, setCallingEnabled] = useState(false);
  const [callButtonVisible, setCallButtonVisible] = useState(true);
  const [bridgeMode, setBridgeMode] = useState("direct");
  const [greetingAudio, setGreetingAudio] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    try {
      const data = await authGet("/api/calling/settings");
      setCallingEnabled(data.status === "ENABLED");
      setCallButtonVisible(data.callIconVisibility !== "DISABLE_ALL");
      setBridgeMode(data.bridgeMode || "direct");
      setGreetingAudio(data.greetingAudio || null);
    } catch (err) {
      toast.error("Could not load calling settings");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await authPost("/api/calling/settings", {
        status: callingEnabled ? "ENABLED" : "DISABLED",
        call_icon_visibility: callButtonVisible ? "DEFAULT" : "DISABLE_ALL",
        bridgeMode,
      });
      toast.success("Calling settings saved");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to save calling settings");
    } finally {
      setSaving(false);
    }
  };

  const handleFileSelected = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setUploading(true);
    try {
      const result = await authUpload("/api/calling/greeting-audio", file);
      setGreetingAudio({ filename: result.filename, originalFilename: result.originalFilename, uploadedAt: new Date().toISOString() });
      toast.success("Greeting audio uploaded");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to upload greeting audio");
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveGreeting = async () => {
    try {
      await authDelete("/api/calling/greeting-audio");
      setGreetingAudio(null);
      toast.success("Greeting audio removed");
    } catch (err) {
      toast.error(err.response?.data?.error || "Failed to remove greeting audio");
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 640 }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 0.5 }}>
        Settings
      </Typography>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 3 }}>
        Control whether this number takes WhatsApp calls at all, how the call button appears, and
        what a caller hears before an agent picks up.
      </Typography>

      <FormControlLabel
        control={<Switch checked={callingEnabled} onChange={(e) => setCallingEnabled(e.target.checked)} />}
        label={callingEnabled ? "Calling enabled" : "Calling disabled — the call button is hidden and inbound calls are rejected"}
        sx={{ display: "flex", mb: 0.5 }}
      />

      <FormControlLabel
        control={<Switch checked={callButtonVisible} onChange={(e) => setCallButtonVisible(e.target.checked)} disabled={!callingEnabled} />}
        label="Show call button to WhatsApp users"
        sx={{ display: "flex", mb: 0.5 }}
      />
      <Typography variant="caption" sx={{ color: "text.secondary", display: "block", ml: 4.5, mb: 3 }}>
        Hiding the button doesn't stop WhatsApp users from calling this number — turn off Calling
        entirely (above) to block that.
      </Typography>

      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        How calls connect
      </Typography>
      <RadioGroup value={bridgeMode} onChange={(e) => setBridgeMode(e.target.value)} sx={{ mb: 3 }}>
        {BRIDGE_MODES.map((mode) => (
          <Box key={mode.value} sx={{ mb: 1 }}>
            <FormControlLabel value={mode.value} control={<Radio size="small" />} label={mode.label} sx={{ mb: 0 }} />
            <Typography variant="caption" sx={{ color: "text.secondary", display: "block", ml: 4.5 }}>
              {mode.description}
            </Typography>
          </Box>
        ))}
      </RadioGroup>

      {bridgeMode === "sfu-auto" && (
        <Box sx={{ mb: 3, p: 2, border: (t) => `1px solid ${t.palette.divider}`, borderRadius: 1 }}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Greeting / hold audio
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary", display: "block", mb: 1.5 }}>
            Plays on a loop to the caller from the moment the call connects until an agent joins.
            Leave unset for silence. MP3, WAV, or OGG, under 5MB.
          </Typography>

          {greetingAudio?.filename ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="body2">{greetingAudio.originalFilename}</Typography>
              <audio controls src={`${SERVER_DOMAIN}/uploads/greetings/${greetingAudio.filename}`} style={{ height: 32, maxWidth: "100%" }} />
              <Box>
                <Button size="small" color="error" onClick={handleRemoveGreeting}>
                  Remove
                </Button>
              </Box>
            </Box>
          ) : (
            <Button size="small" variant="outlined" disabled={uploading} onClick={() => fileInputRef.current?.click()}>
              {uploading ? "Uploading…" : "Upload audio"}
            </Button>
          )}
          <input ref={fileInputRef} type="file" accept="audio/mpeg,audio/mp3,audio/wav,audio/ogg" hidden onChange={handleFileSelected} />
        </Box>
      )}

      <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ mt: 1 }}>
        {saving ? "Saving…" : "Save"}
      </Button>
    </Box>
  );
}
